import {
  Box,
  FormControl,
  FormLabel,
  InputProps,
  styled,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Control,
  Controller,
  ControllerProps,
  useFormState,
} from "react-hook-form";
import { StyledInput } from "@/components/styled";
import { ReactNode, useState, useRef } from "react";
import SVGEyeShow from "@/assets/icons/SVGEyeShow.tsx";
import SVGEyeHide from "@/assets/icons/SVGEyeHide.tsx";
import { usePreventNumberInputScroll } from "@/hooks/usePreventNumberInputScroll";

type FormInputProps = InputProps & {
  name: string;
  control: Control<any, any>;
  label?: ReactNode;
  adornment?: ReactNode;
  controlProps?: Partial<ControllerProps>;
};

const BoxIcon = styled(Box)(() => ({
  position: "absolute",
  right: "15px",
  top: 0,
  transform: "translateY(50%)",
  display: "flex",
  cursor: "pointer",
}));

const FormInput = ({
  name,
  control,
  label,
  controlProps,
  type,
  adornment,
  ...props
}: FormInputProps) => {
  const { palette } = useTheme();
  const isPassword = type === "password";
  const [showPassword, setShowPassword] = useState(false);
  const isRequired = !!controlProps?.rules?.required;

  const formState = useFormState({ control });
  const shouldShowError = formState.isSubmitted ?? true;

  const inputRef = useRef<HTMLInputElement>(null);
  usePreventNumberInputScroll(inputRef, type === "number");

  return (
    <FormControl component="fieldset" sx={{ width: "100%" }}>
      <FormLabel component="legend" sx={{ width: "100%" }}>
        <Box
          sx={{
            fontSize: "12px",
            fontWeight: 500,
            color: (theme) => theme.palette.textGray1,
            paddingBottom: "2px",
            display: "flex",
          }}
        >
          {label}{" "}
          {isRequired && (
            <Typography sx={{ color: "red", paddingLeft: "4px" }}>*</Typography>
          )}
        </Box>
      </FormLabel>
      <Controller
        name={name}
        control={control}
        {...controlProps}
        render={({
          field: { onChange, value },
          fieldState: { error, isTouched, isDirty },
        }) => {
          const hasManualError = error && (error as any).type === "manual";
          const showError =
            (shouldShowError || isTouched || isDirty || hasManualError) && !!error;

          return (
            <Box sx={{ position: "relative", width: "100%" }}>
              <StyledInput
                error={showError}
                onChange={onChange}
                value={value}
                fullWidth
                password={isPassword}
                adornment={!!adornment}
                type={showPassword ? "text" : type}
                {...props}
                inputRef={inputRef}
              />
              {showError && !!error?.message && (
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: palette.errorText,
                    paddingTop: "4px",
                  }}
                >
                  {error?.message}
                </Typography>
              )}
              {isPassword && (
                <BoxIcon onClick={() => setShowPassword(!showPassword)}>
                  {!showPassword ? <SVGEyeShow /> : <SVGEyeHide />}
                </BoxIcon>
              )}
              {!!adornment && <BoxIcon>{adornment}</BoxIcon>}
            </Box>
          );
        }}
      />
    </FormControl>
  );
};

export default FormInput;
